import type { SecondaryDimensionValues } from '@revelio/core';
import {
  isEmptyCompositionBucket,
  isOtherCompositionBucket,
  OTHER_BUCKET_LABEL,
} from '@revelio/core/composition-buckets';
import type { CompositionV4BreakdownReq } from '@revelio/data-access';

import { FilterId, type PrimaryFilterId } from '../../filters/types/filters';
import type {
  TableData,
  TableDimension,
  TableDimensionColumn,
  TableDimensionValue,
} from '../DataProvider/types';
import { selectTableMetrics } from '../DataProvider/utils/table-data';
import type { CompositionV4Metric } from '../PageConfigProvider/types/metric';
import {
  breakdownTopN,
  isRequestedBreakdown,
} from './composition-v4-breakdowns';
import type { CompositionV4Response } from './use-composition-v4-query';

const NO_VALUE_INDEX = 0xffffffff;

const toDimensionColumn = (
  rowValues: readonly (TableDimensionValue | undefined)[]
): TableDimensionColumn => {
  const values: TableDimensionValue[] = [];
  const indexById = new Map<string, number>();
  const indices = Uint32Array.from(rowValues, (value) => {
    if (value === undefined) return NO_VALUE_INDEX;
    const index = indexById.get(value.id);
    if (index !== undefined) return index;
    indexById.set(value.id, values.length);
    values.push(value);
    return values.length - 1;
  });
  return { values, indices };
};

const toMetricColumn = (
  rowValues: readonly (number | null | undefined)[]
): Float64Array => Float64Array.from(rowValues, (value) => value ?? Number.NaN);

type CompositionV4Entity = CompositionV4Response['entities'][number];
type CompositionV4Category =
  CompositionV4Entity['breakdowns'][number]['categories'][number];

export const dimensionFromPrimaryFilter = (
  primaryFilter: PrimaryFilterId
): SecondaryDimensionValues => {
  switch (primaryFilter) {
    case FilterId.COMPANY:
      return 'company';
    case FilterId.INDUSTRY:
      return 'rics';
    case FilterId.GEOGRAPHY:
      return 'geography';
    case FilterId.ROLE:
      return 'role';
    case FilterId.CUSTOM_SKILL:
      return 'skills';
    case FilterId.ACTIVITY:
      return 'activities';
  }
};

const entityValue = (entity: CompositionV4Entity): TableDimensionValue => ({
  id: String(entity.id),
  label: entity.short_name,
});

export const convertCompositionV4Data = ({
  data: { entities },
  primaryDimension,
  metrics,
}: {
  data: CompositionV4Response;
  primaryDimension: PrimaryFilterId;
  metrics: CompositionV4Metric[];
}): TableData => {
  const dimension = dimensionFromPrimaryFilter(primaryDimension);
  return {
    rowCount: entities.length,
    dimensions: [dimension],
    metrics,
    dimensionColumns: {
      [dimension]: toDimensionColumn(entities.map(entityValue)),
    },
    metricColumns: metrics.reduce<TableData['metricColumns']>(
      (columns, metric) => {
        columns[metric] = toMetricColumn(
          entities.map((entity) => {
            const series = entity.metrics[metric] ?? [];
            return series[series.length - 1]?.val;
          })
        );
        return columns;
      },
      {}
    ),
  };
};

export const convertCompositionV4Timeseries = ({
  data: { entities },
  primaryDimension,
  metrics,
  monthRefs,
}: {
  data: CompositionV4Response;
  primaryDimension: PrimaryFilterId;
  metrics: CompositionV4Metric[];
  monthRefs: readonly string[];
}): TableData => {
  const dimension = dimensionFromPrimaryFilter(primaryDimension);
  const months = monthRefs.map(
    (date): TableDimensionValue => ({ id: date, label: date })
  );
  const entityRows: TableDimensionValue[] = [];
  const monthRows: TableDimensionValue[] = [];
  const columns = metrics.map((metric) => ({
    metric,
    rows: [] as (number | undefined)[],
  }));

  for (const entity of entities) {
    const entityRow = entityValue(entity);
    const series = columns.map(
      ({ metric }) =>
        new Map(
          (entity.metrics[metric] ?? []).map(
            ({ id, val }): [number, number] => [id, val]
          )
        )
    );
    const monthIds = new Set<number>();
    for (const points of series) {
      for (const id of points.keys()) monthIds.add(id);
    }

    for (const id of Array.from(monthIds).sort((a, b) => a - b)) {
      const month = months[id];
      if (!month) continue;
      entityRows.push(entityRow);
      monthRows.push(month);
      columns.forEach(({ rows }, index) => {
        rows.push(series[index]?.get(id));
      });
    }
  }

  return {
    rowCount: entityRows.length,
    dimensions: [dimension, 'timeseries'],
    metrics,
    dimensionColumns: {
      [dimension]: toDimensionColumn(entityRows),
      timeseries: toDimensionColumn(monthRows),
    },
    metricColumns: columns.reduce<TableData['metricColumns']>(
      (metricColumns, { metric, rows }) => {
        metricColumns[metric] = toMetricColumn(rows);
        return metricColumns;
      },
      {}
    ),
  };
};

const DIMENSIONS_WITHOUT_OTHER = new Set<TableDimension>([
  'gender',
  'ethnicity',
  'education',
  'seniority',
]);

const isDrawnCategory = (
  dimension: TableDimension,
  { id }: CompositionV4Category
): boolean =>
  !isEmptyCompositionBucket(id) &&
  !(DIMENSIONS_WITHOUT_OTHER.has(dimension) && isOtherCompositionBucket(id));

const categoryLabel = ({ id, short_name }: CompositionV4Category): string =>
  isOtherCompositionBucket(id)
    ? OTHER_BUCKET_LABEL
    : `${short_name.charAt(0).toUpperCase()}${short_name.slice(1)}`;

const addValues = (
  value: number | undefined,
  added: number | undefined
): number | undefined =>
  value === undefined && added === undefined
    ? undefined
    : (value ?? 0) + (added ?? 0);

type CompositionV4Point = CompositionV4Category['headcount'][number];

const latestPoint = ({ headcount }: CompositionV4Category) =>
  headcount[headcount.length - 1];

const addPoints = (
  points: readonly CompositionV4Point[],
  added: readonly CompositionV4Point[]
): CompositionV4Point[] => {
  const byMonth = new Map(points.map((point) => [point.id, point]));
  for (const point of added) {
    const sum = byMonth.get(point.id);
    byMonth.set(
      point.id,
      sum
        ? {
            ...sum,
            count: sum.count + point.count,
            share: sum.share + point.share,
          }
        : point
    );
  }
  return Array.from(byMonth.values()).sort((a, b) => a.id - b.id);
};

const categoriesOf = (
  entity: CompositionV4Entity,
  breakdown: CompositionV4BreakdownReq
): readonly CompositionV4Category[] =>
  entity.breakdowns.find((response) =>
    isRequestedBreakdown(breakdown, response)
  )?.categories ?? [];

const topCategories = (
  categories: readonly CompositionV4Category[],
  topN: number
): CompositionV4Category[] => {
  const top: CompositionV4Category[] = [];
  let other: CompositionV4Category | undefined;
  for (const category of categories) {
    if (isEmptyCompositionBucket(category.id)) continue;
    if (!isOtherCompositionBucket(category.id) && top.length < topN) {
      top.push(category);
      continue;
    }
    other = {
      ...(other ?? category),
      id: -1,
      headcount: addPoints(other?.headcount ?? [], category.headcount),
    };
  }
  return other ? [...top, other] : top;
};

type CategoryRow = {
  value: TableDimensionValue;
  count: number | undefined;
  share: number | undefined;
};

const toCategoryRows = (
  categories: readonly CompositionV4Category[],
  isDrawn: (category: CompositionV4Category) => boolean
): { rows: CategoryRow[]; rowIdOf: Map<string, string> } => {
  const rows: CategoryRow[] = [];
  const rowByLabel = new Map<string, CategoryRow>();
  const rowIdOf = new Map<string, string>();
  for (const category of categories) {
    if (!isDrawn(category)) continue;
    const label = categoryLabel(category);
    const latest = latestPoint(category);
    const row = rowByLabel.get(label);
    if (row) {
      row.count = addValues(row.count, latest?.count);
      row.share = addValues(row.share, latest?.share);
    } else {
      const added = {
        value: { id: String(category.id), label },
        count: latest?.count,
        share: latest?.share,
      };
      rowByLabel.set(label, added);
      rows.push(added);
    }
    rowIdOf.set(String(category.id), row?.value.id ?? String(category.id));
  }
  return { rows, rowIdOf };
};

type BreakdownRow = {
  values: readonly (TableDimensionValue | undefined)[];
  count: number | undefined;
  share: number | undefined;
};

const toBreakdownTable = (
  dimension: SecondaryDimensionValues,
  columns: readonly TableDimension[],
  entityRows: readonly (readonly [TableDimensionValue, BreakdownRow[]])[]
): TableData => {
  const entities: TableDimensionValue[] = [];
  const columnValues = columns.map(
    (): (TableDimensionValue | undefined)[] => []
  );
  const counts: (number | undefined)[] = [];
  const shares: (number | undefined)[] = [];
  for (const [entity, rows] of entityRows) {
    for (const { values, count, share } of rows) {
      entities.push(entity);
      values.forEach((value, index) => {
        columnValues[index]?.push(value);
      });
      counts.push(count);
      shares.push(share);
    }
  }

  return {
    rowCount: entities.length,
    dimensions: [dimension, ...columns],
    metrics: ['headcount', 'share_of_headcount'],
    dimensionColumns: columns.reduce<TableData['dimensionColumns']>(
      (dimensionColumns, column, index) => {
        dimensionColumns[column] = toDimensionColumn(columnValues[index] ?? []);
        return dimensionColumns;
      },
      { [dimension]: toDimensionColumn(entities) }
    ),
    metricColumns: {
      headcount: toMetricColumn(counts),
      share_of_headcount: toMetricColumn(shares),
    },
  };
};

export const convertCompositionV4Breakdown = ({
  data: { entities },
  primaryDimension,
  column,
  breakdown,
}: {
  data: CompositionV4Response;
  primaryDimension: PrimaryFilterId;
  column: TableDimension;
  breakdown: CompositionV4BreakdownReq;
}): TableData =>
  toBreakdownTable(
    dimensionFromPrimaryFilter(primaryDimension),
    [column],
    entities.map((entity) => [
      entityValue(entity),
      toCategoryRows(
        topCategories(
          categoriesOf(entity, breakdown),
          breakdownTopN(breakdown)
        ),
        (category) => isDrawnCategory(column, category)
      ).rows.map(({ value, ...metrics }) => ({
        values: [value],
        ...metrics,
      })),
    ])
  );

export const convertCompositionV4CompaniesByEntity = ({
  data: { entities: companies },
  primaryDimension,
  breakdowns,
}: {
  data: CompositionV4Response;
  primaryDimension: PrimaryFilterId;
  breakdowns: readonly CompositionV4BreakdownReq[];
}): TableData =>
  selectTableMetrics(
    toBreakdownTable(
      dimensionFromPrimaryFilter(primaryDimension),
      ['company'],
      breakdowns.flatMap((breakdown) =>
        (breakdown.ids ?? []).flatMap((id) => {
          const cells = companies.flatMap((company) => {
            const category = categoriesOf(company, breakdown).find(
              (candidate) => candidate.id === id
            );
            return category ? [{ company, category }] : [];
          });
          const [first] = cells;
          if (!first) return [];
          const entity = {
            id: String(id),
            label: categoryLabel(first.category),
          };
          return [
            [
              entity,
              cells.map(({ company, category }) => ({
                values: [entityValue(company)],
                count: latestPoint(category)?.count,
                share: undefined,
              })),
            ] as const,
          ];
        })
      )
    ),
    ['headcount']
  );

export const convertCompositionV4BreakdownSeries = ({
  data: { entities },
  primaryDimension,
  column,
  breakdown,
  monthRefs,
}: {
  data: CompositionV4Response;
  primaryDimension: PrimaryFilterId;
  column: TableDimension;
  breakdown: CompositionV4BreakdownReq;
  monthRefs: readonly string[];
}): TableData => {
  const months = monthRefs.map(
    (date): TableDimensionValue => ({ id: date, label: date })
  );
  return toBreakdownTable(
    dimensionFromPrimaryFilter(primaryDimension),
    [column, 'timeseries'],
    entities.map((entity) => [
      entityValue(entity),
      topCategories(categoriesOf(entity, breakdown), breakdownTopN(breakdown))
        .filter((category) => isDrawnCategory(column, category))
        .flatMap((category) => {
          const value = {
            id: String(category.id),
            label: categoryLabel(category),
          };
          return category.headcount.flatMap(
            ({ id, count, share }): BreakdownRow[] => {
              const month = months[id];
              return month ? [{ values: [value, month], count, share }] : [];
            }
          );
        }),
    ])
  );
};

const OTHER_VALUE: TableDimensionValue = {
  id: '-1',
  label: OTHER_BUCKET_LABEL,
};

const sumOf = (
  rows: readonly CategoryRow[],
  metric: 'count' | 'share'
): number | undefined =>
  rows.reduce<number | undefined>(
    (sum, row) => addValues(sum, row[metric]),
    undefined
  );

const remainderOf = (
  parent: CategoryRow,
  children: readonly CategoryRow[]
): Omit<BreakdownRow, 'values'> | undefined => {
  const count =
    parent.count === undefined
      ? undefined
      : parent.count - (sumOf(children, 'count') ?? 0);
  if (count === undefined || count <= 0) return undefined;
  return {
    count,
    share:
      parent.share === undefined
        ? undefined
        : parent.share - (sumOf(children, 'share') ?? 0),
  };
};

export const convertCompositionV4BreakdownTree = ({
  data: { entities },
  primaryDimension,
  columns,
  breakdowns: [breakdown, sublevel],
  parentOf,
}: {
  data: CompositionV4Response;
  primaryDimension: PrimaryFilterId;
  columns: readonly [TableDimension, TableDimension];
  breakdowns: readonly [CompositionV4BreakdownReq, CompositionV4BreakdownReq];
  parentOf: ReadonlyMap<string, { parentId: string }>;
}): TableData =>
  toBreakdownTable(
    dimensionFromPrimaryFilter(primaryDimension),
    columns,
    entities.map((entity) => {
      const parents = toCategoryRows(
        topCategories(
          categoriesOf(entity, breakdown),
          breakdownTopN(breakdown)
        ),
        (category) => isDrawnCategory(columns[0], category)
      );
      const childrenOf = new Map<string, CompositionV4Category[]>();
      for (const category of categoriesOf(entity, sublevel)) {
        if (
          isOtherCompositionBucket(category.id) ||
          isEmptyCompositionBucket(category.id)
        ) {
          continue;
        }
        const parentId = parentOf.get(String(category.id))?.parentId;
        const parent =
          parentId === undefined ? undefined : parents.rowIdOf.get(parentId);
        if (parent === undefined || isOtherCompositionBucket(parent)) continue;
        childrenOf.set(parent, [...(childrenOf.get(parent) ?? []), category]);
      }

      const rows: BreakdownRow[] = [];
      for (const parent of parents.rows) {
        rows.push({
          values: [parent.value, undefined],
          count: parent.count,
          share: parent.share,
        });
        const children = toCategoryRows(
          childrenOf.get(parent.value.id) ?? [],
          () => true
        ).rows;
        for (const { value, ...metrics } of children) {
          rows.push({ values: [parent.value, value], ...metrics });
        }
        const remainder = remainderOf(parent, children);
        if (remainder) {
          rows.push({ values: [parent.value, OTHER_VALUE], ...remainder });
        }
      }
      return [entityValue(entity), rows];
    })
  );
